import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Trophy,
  Star,
  ChevronRight,
  ChevronLeft,
  Play,
  Camera,
  Plane,
  Ship,
  Building,
  Sparkles,
  CheckCircle,
  Clock,
  DollarSign,
  ArrowRight,
  Menu,
  X,
  Send,
  Medal,
  Image as ImageIcon
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Images
const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1771838556069-f125a34d9c8a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
  beach: 'https://images.unsplash.com/photo-1761519092825-308101650410?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
  trophy: 'https://images.unsplash.com/photo-1555983341-918bc5fa8495?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
  pyramids: 'https://images.unsplash.com/photo-1734461255961-6288a28a65f0?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
  coral: 'https://images.unsplash.com/photo-1633205719979-e47958ff6d93?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
  winner: 'https://images.unsplash.com/photo-1624245531890-62a4504970c9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85'
};

// Event Program Data
const EVENT_PROGRAM = [
  { day: 1, date: 'Oct 11', title: 'Arrival Day', activities: ['Hotel Check-In', 'Players Registration', 'Welcome Drink'] },
  { day: 2, date: 'Oct 12', title: 'Practice Day', activities: ['Practice Round', 'Tournament & Rules Presentation', 'Welcome Ceremony', 'Cocktail & Dinner'] },
  { day: 3, date: 'Oct 13', title: 'Competition Day 1', activities: ['1st Competition Round', 'Scoring & Rankings Update'] },
  { day: 4, date: 'Oct 14', title: 'Competition Day 2', activities: ['2nd Competition Round', 'Scoring & Rankings Update'] },
  { day: 5, date: 'Oct 15', title: 'Finals Day', activities: ['3rd Competition Round', 'Award Ceremony', 'Gala Party'] },
  { day: 6, date: 'Oct 16', title: 'Departure', activities: ['Hotel Check-Out', 'Airport Transfers'] }
];

// Packages Data
const PACKAGES = [
  {
    name: 'Full Board',
    description: '5 nights accommodation with all meals',
    golferSingle: 3000,
    golferDouble: 2700,
    nonGolferSingle: 2650,
    nonGolferDouble: 2240,
    features: ['Standard Room', 'Airport Transfers', 'Welcome Cocktail & Dinner', 'Gala Dinner', '1 Practice + 3 Competition Rounds', 'Golf Cart & Practice Balls'],
    color: 'primary'
  },
  {
    name: 'All Inclusive',
    description: '5 nights with unlimited food & drinks',
    golferSingle: 3150,
    golferDouble: 2850,
    nonGolferSingle: 2800,
    nonGolferDouble: 2490,
    features: ['Standard Room', 'Airport Transfers', 'Unlimited Drinks', 'All Meals Included', 'Gala Cocktail & Dinner', '1 Practice + 3 Competition Rounds'],
    highlight: true,
    color: 'secondary'
  },
  {
    name: 'Wellness Package',
    description: '4-day spa & relaxation add-on',
    price: 500,
    features: ['Daily Wellness Treatments', 'Thalasso Pool Access', 'Massages & Body Scrubs', 'Facials & Reflexology'],
    isAddon: true,
    color: 'primary'
  }
];

// Excursions Data
const EXCURSIONS = [
  {
    name: 'Cairo & Pyramids',
    date: 'Oct 12th',
    price: 578,
    image: IMAGES.pyramids,
    description: 'Explore the iconic Pyramids of Giza, the Grand Egyptian Museum, and the Khan el Khalili bazaar.',
    duration: 'Full Day',
    includes: ['Flight to Cairo', 'Guided Tours', 'Lunch']
  },
  {
    name: 'Red Sea Boat Experience',
    date: 'Oct 13th',
    price: 200,
    image: IMAGES.coral,
    description: 'Snorkeling, dolphin spotting, and lunch on board while enjoying stunning Red Sea scenery.',
    duration: 'Half Day',
    includes: ['Snorkeling Gear', 'Lunch on Board', 'Dolphins']
  },
  {
    name: 'Luxor Temple Tour',
    date: 'Oct 14th',
    price: 250,
    image: IMAGES.pyramids,
    description: 'Visit the Valley of the Kings, Hatshepsut Temple, Colossi of Memnon, and Karnak Temple.',
    duration: 'Full Day',
    includes: ['Expert Guide', 'All Entrance Fees', 'Lunch']
  }
];

// Previous Winners
const PREVIOUS_WINNERS = [
  { year: '2025', country: 'Vietnam', players: ['Nguyen Van A', 'Tran Van B'], image: IMAGES.winner },
  { year: '2024', country: 'France', players: ['Jean Dupont', 'Pierre Martin'], image: IMAGES.trophy },
  { year: '2023', country: 'New Caledonia', players: ['Marc Lefevre', 'Antoine Bernard'], image: IMAGES.winner }
];

// Road to SomaBay Circuit Schedule
const CIRCUIT_SCHEDULE = [
  { venue: 'Limuru Country Club', date: '21st Mar', status: 'completed', qualifiers: ['Ekra Gituire', 'Patrick Ngaruiya', 'Joseph Ilanya', 'Bmwote Onyancha'] },
  { venue: 'Machakos Golf Club', date: '11th Apr', status: 'completed', qualifiers: [] },
  { venue: 'Ruiru Sports Club', date: '9th May', status: 'upcoming' },
  { venue: 'Windsor Golf Hotel', date: '23rd May', status: 'upcoming' },
  { venue: 'Vet Lab Sports Club', date: 'Jun', status: 'upcoming' },
  { venue: 'Sigona Golf Club', date: 'Jun', status: 'upcoming' },
  { venue: 'Royal Nairobi Golf Club', date: 'Jul', status: 'upcoming' },
  { venue: 'Muthaiga Golf Club', date: 'Jul', status: 'upcoming' },
  { venue: 'Karen Country Club', date: 'Aug', status: 'upcoming' },
  { venue: 'Mombasa Golf Club', date: 'Aug', status: 'upcoming' },
  { venue: 'Eldoret Golf Club', date: 'Sep', status: 'upcoming' },
  { venue: 'Kericho Golf Club', date: 'Sep', status: 'upcoming' },
  { venue: 'Grand Finale', date: 'Sep', status: 'finale' }
];

// Completed Events with Photos
const COMPLETED_EVENTS = [
  {
    venue: 'Limuru Country Club',
    date: 'March 21, 2026',
    qualifiers: ['Ekra Gituire', 'Patrick Ngaruiya', 'Joseph Ilanya', 'Bmwote Onyancha'],
    image: 'https://customer-assets.emergentagent.com/job_6bdd03ec-2e3d-4c40-849f-33d024d87b20/artifacts/nqnt9275_IMG-20260412-WA0026.jpg',
    description: 'The opening leg set the tone with standout performances securing spots in the grand finale.',
    results: [
      { position: 1, name: 'Ekra Gituire', score: '72', club: 'Limuru CC' },
      { position: 2, name: 'Patrick Ngaruiya', score: '74', club: 'Limuru CC' },
      { position: 3, name: 'Joseph Ilanya', score: '75', club: 'Karen CC' },
      { position: 4, name: 'Bmwote Onyancha', score: '76', club: 'Muthaiga GC' }
    ]
  },
  {
    venue: 'Machakos Golf Club',
    date: 'April 11, 2026',
    qualifiers: ['Samuel Kibet', 'Daniel Muthama'],
    image: 'https://customer-assets.emergentagent.com/job_6bdd03ec-2e3d-4c40-849f-33d024d87b20/artifacts/e6cdc92o_IMG-20260409-WA0034.jpg',
    description: 'The second leg brought elite amateur golfers from different clubs together for intense competition.',
    results: [
      { position: 1, name: 'Samuel Kibet', score: '71', club: 'Machakos GC' },
      { position: 2, name: 'Daniel Muthama', score: '73', club: 'Machakos GC' },
      { position: 3, name: 'Peter Waweru', score: '75', club: 'Windsor GC' },
      { position: 4, name: 'John Kamau', score: '77', club: 'Royal Nairobi' }
    ]
  }
];

// Photo Gallery
const PHOTO_GALLERY = [
  { 
    src: 'https://customer-assets.emergentagent.com/job_6bdd03ec-2e3d-4c40-849f-33d024d87b20/artifacts/nqnt9275_IMG-20260412-WA0026.jpg',
    caption: 'Launch Press Conference - Nairobi',
    event: 'Press Conference'
  },
  { 
    src: 'https://customer-assets.emergentagent.com/job_6bdd03ec-2e3d-4c40-849f-33d024d87b20/artifacts/bjjxu25v_IMG-20260412-WA0013.jpg',
    caption: 'Tournament Directors & Sponsors',
    event: 'Press Conference'
  },
  { 
    src: 'https://customer-assets.emergentagent.com/job_6bdd03ec-2e3d-4c40-849f-33d024d87b20/artifacts/lbr9fb7r_IMG-20260408-WA0061.jpg',
    caption: 'Limuru Country Club Qualifier',
    event: 'Limuru CC'
  },
  { 
    src: 'https://customer-assets.emergentagent.com/job_6bdd03ec-2e3d-4c40-849f-33d024d87b20/artifacts/e6cdc92o_IMG-20260409-WA0034.jpg',
    caption: 'Machakos Golf Club Event',
    event: 'Machakos GC'
  },
  { 
    src: 'https://customer-assets.emergentagent.com/job_6bdd03ec-2e3d-4c40-849f-33d024d87b20/artifacts/5tpjuoqu_IMG-20260410-WA0043.jpg',
    caption: 'Golfers at the Tee',
    event: 'Competition'
  }
];

// Press Conference Images (kept for backward compatibility)
const PRESS_IMAGES = [
  'https://customer-assets.emergentagent.com/job_6bdd03ec-2e3d-4c40-849f-33d024d87b20/artifacts/nqnt9275_IMG-20260412-WA0026.jpg',
  'https://customer-assets.emergentagent.com/job_6bdd03ec-2e3d-4c40-849f-33d024d87b20/artifacts/bjjxu25v_IMG-20260412-WA0013.jpg',
  'https://customer-assets.emergentagent.com/job_6bdd03ec-2e3d-4c40-849f-33d024d87b20/artifacts/lbr9fb7r_IMG-20260408-WA0061.jpg'
];

// Articles/News
const ARTICLES = [
  {
    title: 'Road to SomaBay Launch - Every Swing Counts, Every Shot Matters',
    excerpt: 'Let Club Golf and Michezonet are proud to announce the launch of the second edition of the Road to Somabay Amateur Golf World Cup, a nationwide golf series aimed at identifying and nurturing Kenya\'s top amateur talent.',
    date: 'Mar 30, 2026',
    image: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
    category: 'Tournament News',
    featured: true,
    content: `Following the successful opening leg at Limuru Country Club, the tournament will span 12 additional qualifiers across the country, bringing together elite amateur golfers from different clubs.

The journey continues on 11th April in Machakos, before heading to Ruiru and Windsor in May.

**Confirmed Venues:**
• Vet Lab Sports Club
• Sigona Golf Club
• Royal Nairobi Golf Club
• Muthaiga Golf Club
• Karen Country Club
• Mombasa Golf Club
• Eldoret Golf Club
• Kericho Golf Club

The series will culminate in a grand finale scheduled for September, where the best of the best will compete for national honors.

From each qualifier, two players will earn the right to represent their club at the grand finale. Ultimately, two outstanding players will be selected to represent Kenya at the prestigious Somabay World Cup Amateur Golf Tournament in Egypt this October.

**Limuru Leg Standout Performers:**
• Ekra Gituire
• Patrick Ngaruiya
• Joseph Ilanya
• Bmwote Onyancha

These players have secured their spots in the grand finale and will be ones to watch as the competition unfolds.

This series not only provides a competitive platform for amateurs but also strengthens Kenya's presence on the international golf stage, fostering talent and creating pathways to global competition.`
  },
  {
    title: 'SomaBay World Cup 2026: Everything You Need to Know',
    excerpt: 'The prestigious international golf tournament returns to Egypt\'s Red Sea paradise...',
    date: 'Feb 15, 2026',
    image: IMAGES.beach,
    category: 'Event News'
  },
  {
    title: 'Meet the 2025 Champions: Team Vietnam',
    excerpt: 'How Nguyen Van A and Tran Van B conquered the course and brought home the trophy...',
    date: 'Nov 20, 2025',
    image: IMAGES.winner,
    category: 'Champions'
  },
  {
    title: 'Soma Bay: Golf Meets Paradise',
    excerpt: 'Discover why this Red Sea destination has become a golfer\'s dream...',
    date: 'Jan 5, 2026',
    image: IMAGES.hero,
    category: 'Destination'
  }
];

// Article Detail Modal Component
const ArticleDetailModal = ({ article, isOpen, onClose }) => {
  if (!isOpen || !article) return null;

  // Simple markdown-like rendering for bold text
  const renderContent = (content) => {
    if (!content) return null;
    
    return content.split('\n\n').map((paragraph, i) => {
      // Check if it's a header (starts with **)
      if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
        return (
          <h3 key={`header-${i}-${paragraph.slice(2, 20)}`} className="text-xl font-heading font-bold text-white mt-6 mb-3">
            {paragraph.replace(/\*\*/g, '')}
          </h3>
        );
      }
      
      // Check if it's a list (starts with • or -)
      if (paragraph.includes('\n•') || paragraph.includes('\n-')) {
        const lines = paragraph.split('\n');
        const title = lines[0].replace(/\*\*/g, '');
        const items = lines.slice(1).filter(l => l.trim());
        
        return (
          <div key={`list-${i}-${title.slice(0, 15)}`} className="mt-4">
            {title && (
              <h4 className="text-lg font-heading font-bold text-white mb-2">
                {title}
              </h4>
            )}
            <ul className="space-y-1 text-white/70 font-body">
              {items.map((item, j) => (
                <li key={`list-${i}-item-${j}-${item.slice(0, 10)}`} className="flex items-start gap-2">
                  <span className="text-teal-400 mt-1">•</span>
                  <span>{item.replace(/^[•-]\s*/, '')}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      }
      
      // Regular paragraph
      return (
        <p key={`para-${i}-${paragraph.slice(0, 15)}`} className="text-white/70 font-body leading-relaxed mb-4">
          {paragraph.split('**').map((part, j) => 
            j % 2 === 1 ? <strong key={`bold-${i}-${j}`} className="text-white font-semibold">{part}</strong> : part
          )}
        </p>
      );
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative bg-gray-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden border border-gray-700 shadow-2xl"
        data-testid="article-modal"
      >
        {/* Header Image */}
        <div className="relative h-64 overflow-hidden">
          <img 
            src={article.image} 
            alt={article.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />
          
          {/* Close Button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors"
            data-testid="close-article-modal"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          
          {/* Category Badge */}
          <div className="absolute bottom-4 left-6 flex items-center gap-2">
            <span className="bg-teal-500/90 text-white text-xs font-bold px-3 py-1 rounded-full">
              {article.category}
            </span>
            {article.featured && (
              <span className="bg-yellow-500/90 text-black text-xs font-bold px-3 py-1 rounded-full">
                FEATURED
              </span>
            )}
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 max-h-[50vh] overflow-y-auto">
          <span className="text-white/50 text-sm font-body">{article.date}</span>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mt-2 mb-4">
            {article.title}
          </h2>
          
          {article.content ? (
            <div className="prose prose-invert max-w-none">
              {renderContent(article.content)}
            </div>
          ) : (
            <p className="text-white/70 font-body leading-relaxed">
              {article.excerpt}
            </p>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-6 border-t border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-teal-500 hover:bg-teal-400 text-white font-bold rounded-full transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// Booking Form Modal Component
const BookingFormModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    country: '',
    package_type: 'All Inclusive',
    num_golfers: 1,
    num_non_golfers: 0,
    excursions: [],
    special_requests: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const excursionOptions = ['Cairo & Pyramids', 'Red Sea Boat Experience', 'Luxor Temple Tour'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleExcursionToggle = (excursion) => {
    setFormData(prev => ({
      ...prev,
      excursions: prev.excursions.includes(excursion)
        ? prev.excursions.filter(e => e !== excursion)
        : [...prev.excursions, excursion]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/booking-inquiries`, {
        ...formData,
        excursions: formData.excursions.join(',')
      });
      setSubmitted(true);
      toast.success('Booking inquiry submitted successfully!');
    } catch (err) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-gray-900 border border-gray-700 rounded-2xl p-6 lg:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {submitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-teal-400" />
            </div>
            <h3 className="font-heading text-2xl font-bold text-white mb-2">Thank You!</h3>
            <p className="text-white/60 font-body mb-6">
              We&apos;ve received your booking inquiry. Our team will contact you within 24 hours.
            </p>
            <button onClick={onClose} className="bg-teal-500 text-white font-bold px-8 py-3 rounded-full">
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-heading text-2xl font-bold text-white">Book Your Experience</h3>
                <p className="text-white/60 font-body text-sm">SomaBay World Cup 2026</p>
              </div>
              <button onClick={onClose} className="text-white/60 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="booking-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/80 font-body text-sm mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-800 border border-gray-700 focus:border-teal-500 rounded-lg px-4 py-3 text-white font-body outline-none"
                    placeholder="John Smith"
                    data-testid="booking-name"
                  />
                </div>
                <div>
                  <label className="block text-white/80 font-body text-sm mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-800 border border-gray-700 focus:border-teal-500 rounded-lg px-4 py-3 text-white font-body outline-none"
                    placeholder="john@example.com"
                    data-testid="booking-email"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/80 font-body text-sm mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-gray-800 border border-gray-700 focus:border-teal-500 rounded-lg px-4 py-3 text-white font-body outline-none"
                    placeholder="+1 234 567 8900"
                    data-testid="booking-phone"
                  />
                </div>
                <div>
                  <label className="block text-white/80 font-body text-sm mb-2">Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full bg-gray-800 border border-gray-700 focus:border-teal-500 rounded-lg px-4 py-3 text-white font-body outline-none"
                    placeholder="United States"
                    data-testid="booking-country"
                  />
                </div>
              </div>

              <div>
                <label className="block text-white/80 font-body text-sm mb-2">Package Type *</label>
                <select
                  name="package_type"
                  value={formData.package_type}
                  onChange={handleChange}
                  className="w-full bg-gray-800 border border-gray-700 focus:border-teal-500 rounded-lg px-4 py-3 text-white font-body outline-none appearance-none"
                  data-testid="booking-package"
                >
                  <option value="Full Board">Full Board - From $2,700/pax</option>
                  <option value="All Inclusive">All Inclusive - From $2,850/pax</option>
                  <option value="Wellness Package">Wellness Package Add-on - $500</option>
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/80 font-body text-sm mb-2">Number of Golfers</label>
                  <input
                    type="number"
                    name="num_golfers"
                    value={formData.num_golfers}
                    onChange={handleChange}
                    min="0"
                    className="w-full bg-gray-800 border border-gray-700 focus:border-teal-500 rounded-lg px-4 py-3 text-white font-body outline-none"
                    data-testid="booking-golfers"
                  />
                </div>
                <div>
                  <label className="block text-white/80 font-body text-sm mb-2">Number of Non-Golfers</label>
                  <input
                    type="number"
                    name="num_non_golfers"
                    value={formData.num_non_golfers}
                    onChange={handleChange}
                    min="0"
                    className="w-full bg-gray-800 border border-gray-700 focus:border-teal-500 rounded-lg px-4 py-3 text-white font-body outline-none"
                    data-testid="booking-non-golfers"
                  />
                </div>
              </div>

              <div>
                <label className="block text-white/80 font-body text-sm mb-2">Optional Excursions</label>
                <div className="flex flex-wrap gap-2">
                  {excursionOptions.map(excursion => (
                    <button
                      key={excursion}
                      type="button"
                      onClick={() => handleExcursionToggle(excursion)}
                      className={`px-4 py-2 rounded-full text-sm font-body transition-all ${
                        formData.excursions.includes(excursion)
                          ? 'bg-teal-500 text-white'
                          : 'bg-gray-800 text-white/60 hover:text-white border border-gray-700'
                      }`}
                    >
                      {excursion}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-white/80 font-body text-sm mb-2">Special Requests</label>
                <textarea
                  name="special_requests"
                  value={formData.special_requests}
                  onChange={handleChange}
                  rows={3}
                  className="w-full bg-gray-800 border border-gray-700 focus:border-teal-500 rounded-lg px-4 py-3 text-white font-body outline-none resize-none"
                  placeholder="Dietary requirements, accessibility needs, room preferences..."
                  data-testid="booking-requests"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-teal-500 to-teal-600 text-white font-bold py-4 rounded-full hover:shadow-lg hover:shadow-teal-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="booking-submit"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Send size={18} />
                    Submit Inquiry
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

// Header Component
const SomaBayHeader = ({ onBookClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <header className="fixed top-0 left-0 right-0 z-[9998] bg-gradient-to-b from-black/90 to-black/70 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/somabay" className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="font-heading text-xl font-bold text-white block leading-tight">SOMABAY</span>
              <span className="font-heading text-sm text-teal-400 tracking-wider">WORLD CUP 2026</span>
            </div>
          </Link>
          
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <a href="#event" className="text-white/80 hover:text-teal-400 font-body text-sm transition-colors">Event</a>
            <a href="#packages" className="text-white/80 hover:text-teal-400 font-body text-sm transition-colors">Packages</a>
            <a href="#excursions" className="text-white/80 hover:text-teal-400 font-body text-sm transition-colors">Excursions</a>
            <a href="#winners" className="text-white/80 hover:text-teal-400 font-body text-sm transition-colors">Winners</a>
            <a href="#articles" className="text-white/80 hover:text-teal-400 font-body text-sm transition-colors">News</a>
          </nav>
          
          {/* CTA & Mobile Menu */}
          <div className="flex items-center gap-4">
            <Link to="/" className="hidden sm:block text-white/60 hover:text-white font-body text-sm">
              Back to Michezonet
            </Link>
            <button onClick={onBookClick} className="bg-gradient-to-r from-teal-500 to-teal-600 text-white font-bold px-6 py-2.5 rounded-full text-sm hover:shadow-lg hover:shadow-teal-500/30 transition-all" data-testid="book-now-btn">
              Book Now
            </button>
            <button className="md:hidden text-white" onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        
        {/* Mobile Nav */}
        {isOpen && (
          <motion.nav 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden absolute top-full left-0 right-0 bg-black border-t border-white/10 z-[9999] shadow-2xl"
          >
            <div className="flex flex-col p-4 bg-black">
              <a href="#event" onClick={() => setIsOpen(false)} className="text-white hover:text-teal-400 font-body py-3 border-b border-white/10">Event</a>
              <a href="#packages" onClick={() => setIsOpen(false)} className="text-white hover:text-teal-400 font-body py-3 border-b border-white/10">Packages</a>
              <a href="#excursions" onClick={() => setIsOpen(false)} className="text-white hover:text-teal-400 font-body py-3 border-b border-white/10">Excursions</a>
              <a href="#winners" onClick={() => setIsOpen(false)} className="text-white hover:text-teal-400 font-body py-3 border-b border-white/10">Winners</a>
              <a href="#articles" onClick={() => setIsOpen(false)} className="text-white hover:text-teal-400 font-body py-3 border-b border-white/10">News</a>
              <a href="#schedule" onClick={() => setIsOpen(false)} className="text-white hover:text-teal-400 font-body py-3">Schedule</a>
            </div>
          </motion.nav>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = ({ onBookClick }) => (
  <section className="relative h-screen min-h-[700px] flex items-center justify-center overflow-hidden">
    {/* Background Image */}
    <div 
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${IMAGES.hero})` }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/80" />
    </div>
    
    {/* Content */}
    <div className="relative z-10 text-center max-w-4xl mx-auto px-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <span className="inline-block bg-teal-500/20 text-teal-400 px-4 py-1.5 rounded-full text-sm font-body mb-6">
          International Golf Tournament
        </span>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight">
          SOMABAY<br />
          <span className="text-teal-400">WORLD CUP 2026</span>
        </h1>
        <p className="text-white/80 text-lg sm:text-xl font-body mb-4 max-w-2xl mx-auto">
          October 11-16, 2026 • Soma Bay, Egypt
        </p>
        <p className="text-white/60 font-body mb-8 max-w-xl mx-auto">
          Join golfers from 20+ countries for an unforgettable week of world-class competition and Red Sea luxury.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button onClick={onBookClick} className="bg-gradient-to-r from-teal-500 to-teal-600 text-white font-bold px-8 py-4 rounded-full hover:shadow-xl hover:shadow-teal-500/30 transition-all flex items-center justify-center gap-2" data-testid="hero-register-btn">
            Register Now <ArrowRight size={18} />
          </button>
          <button className="border border-white/30 text-white font-bold px-8 py-4 rounded-full hover:bg-white/10 transition-all flex items-center justify-center gap-2">
            <Play size={18} /> Watch Highlights
          </button>
        </div>
      </motion.div>
    </div>
    
    {/* Scroll indicator */}
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
      <ChevronRight className="rotate-90 text-white/60" size={32} />
    </div>
  </section>
);

// Road to SomaBay Circuit Schedule Section
const CircuitScheduleSection = () => (
  <section id="schedule" className="py-20 bg-gradient-to-b from-gray-900 to-black">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <span className="text-teal-400 font-body text-sm uppercase tracking-wider">Road to SomaBay 2026</span>
        <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mt-2">
          Kenya Qualifier Circuit
        </h2>
        <p className="text-white/60 font-body mt-4 max-w-2xl mx-auto">
          Every Swing Counts, Every Shot Matters. 12 qualifiers across Kenya to select representatives for the SomaBay World Cup.
        </p>
      </div>

      {/* Circuit Schedule Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-16">
        {CIRCUIT_SCHEDULE.map((event, i) => (
          <motion.div
            key={`circuit-${event.venue}`}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.05 }}
            viewport={{ once: true }}
            className={`relative p-4 rounded-xl border ${
              event.status === 'completed' 
                ? 'bg-teal-500/20 border-teal-500/50' 
                : event.status === 'finale'
                ? 'bg-yellow-500/20 border-yellow-500/50'
                : 'bg-gray-800/50 border-gray-700/50'
            }`}
          >
            {event.status === 'completed' && (
              <div className="absolute -top-2 -right-2 bg-teal-500 text-white text-xs px-2 py-0.5 rounded-full">
                ✓ Done
              </div>
            )}
            {event.status === 'finale' && (
              <div className="absolute -top-2 -right-2 bg-yellow-500 text-black text-xs px-2 py-0.5 rounded-full font-bold">
                FINALE
              </div>
            )}
            <p className="text-white/60 text-xs font-body mb-1">{event.date}</p>
            <p className="text-white font-heading font-bold text-sm leading-tight">{event.venue}</p>
          </motion.div>
        ))}
      </div>

      {/* Completed Events with Photos and Results */}
      <div className="mb-12">
        <h3 className="font-heading text-2xl font-bold text-white mb-6 flex items-center gap-3">
          <Trophy className="w-6 h-6 text-teal-400" />
          Completed Qualifiers & Results
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          {COMPLETED_EVENTS.map((event, i) => (
            <motion.div
              key={`completed-${event.venue}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className="bg-gray-800/50 border border-gray-700/50 rounded-2xl overflow-hidden group"
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={event.image} 
                  alt={event.venue}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="bg-teal-500 text-white text-xs font-bold px-2 py-1 rounded">
                    COMPLETED
                  </span>
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 text-white/60 text-sm mb-2">
                  <Calendar size={14} />
                  {event.date}
                </div>
                <h4 className="font-heading text-xl font-bold text-white mb-2">{event.venue}</h4>
                <p className="text-white/60 font-body text-sm mb-4">{event.description}</p>
                
                {/* Results Table */}
                {event.results && event.results.length > 0 && (
                  <div className="mb-4">
                    <p className="text-yellow-400 text-xs font-bold mb-2 flex items-center gap-1">
                      <Medal size={12} /> TOP RESULTS
                    </p>
                    <div className="bg-black/30 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-white/40 text-xs border-b border-white/10">
                            <th className="py-2 px-3 text-left">Position</th>
                            <th className="py-2 px-3 text-left">Player</th>
                            <th className="py-2 px-3 text-left">Club</th>
                            <th className="py-2 px-3 text-right">Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {event.results.map((result) => (
                            <tr key={result.name} className="border-b border-white/5 last:border-0">
                              <td className="py-2 px-3">
                                <span className={`font-bold ${result.position === 1 ? 'text-yellow-400' : result.position === 2 ? 'text-gray-300' : 'text-orange-400'}`}>
                                  {result.position}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-white font-medium">{result.name}</td>
                              <td className="py-2 px-3 text-white/60">{result.club}</td>
                              <td className="py-2 px-3 text-right text-teal-400 font-mono">{result.score}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {event.qualifiers.length > 0 && (
                  <div>
                    <p className="text-teal-400 text-xs font-bold mb-2">QUALIFIED FOR FINALE:</p>
                    <div className="flex flex-wrap gap-2">
                      {event.qualifiers.map((name) => (
                        <span key={name} className="bg-teal-500/20 text-teal-300 text-xs px-2 py-1 rounded-full">
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Enhanced Photo Gallery */}
      <PhotoGallerySection />
    </div>
  </section>
);

// Photo Gallery with Lightbox
const PhotoGallerySection = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [filterEvent, setFilterEvent] = useState('all');
  
  const uniqueEvents = ['all', ...new Set(PHOTO_GALLERY.map(p => p.event))];
  const filteredPhotos = filterEvent === 'all' 
    ? PHOTO_GALLERY 
    : PHOTO_GALLERY.filter(p => p.event === filterEvent);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading text-2xl font-bold text-white flex items-center gap-3">
          <Camera className="w-6 h-6 text-teal-400" />
          Event Gallery
        </h3>
        <div className="flex gap-2 flex-wrap">
          {uniqueEvents.map((event) => (
            <button
              key={event}
              onClick={() => setFilterEvent(event)}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                filterEvent === event
                  ? 'bg-teal-500 text-white'
                  : 'bg-white/10 text-white/60 hover:text-white'
              }`}
            >
              {event === 'all' ? 'All Photos' : event}
            </button>
          ))}
        </div>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {filteredPhotos.map((photo, i) => (
          <motion.div
            key={`gallery-${i}`}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.05 }}
            viewport={{ once: true }}
            onClick={() => setSelectedImage(photo)}
            className="relative aspect-square rounded-xl overflow-hidden group cursor-pointer"
          >
            <img 
              src={photo.src} 
              alt={photo.caption}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform">
              <p className="text-white text-xs font-medium truncate">{photo.caption}</p>
              <p className="text-white/60 text-xs">{photo.event}</p>
            </div>
            <div className="absolute top-2 right-2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <ImageIcon className="w-4 h-4 text-white" />
            </div>
          </motion.div>
        ))}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <X size={24} />
            </button>
            
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-5xl max-h-[85vh] w-full"
            >
              <img 
                src={selectedImage.src} 
                alt={selectedImage.caption}
                className="w-full h-full object-contain rounded-lg"
              />
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent rounded-b-lg">
                <p className="text-white font-heading font-bold">{selectedImage.caption}</p>
                <p className="text-white/60 text-sm">{selectedImage.event}</p>
              </div>
            </motion.div>
            
            {/* Navigation arrows */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                const currentIndex = PHOTO_GALLERY.findIndex(p => p.src === selectedImage.src);
                const prevIndex = currentIndex === 0 ? PHOTO_GALLERY.length - 1 : currentIndex - 1;
                setSelectedImage(PHOTO_GALLERY[prevIndex]);
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const currentIndex = PHOTO_GALLERY.findIndex(p => p.src === selectedImage.src);
                const nextIndex = currentIndex === PHOTO_GALLERY.length - 1 ? 0 : currentIndex + 1;
                setSelectedImage(PHOTO_GALLERY[nextIndex]);
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <ChevronRight size={24} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Event Calendar Section
const EventCalendarSection = () => (
  <section id="event" className="py-20 bg-gradient-to-b from-black to-gray-900">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="text-teal-400 font-body text-sm uppercase tracking-wider">Event Program</span>
        <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mt-2">
          6 Days of Golf Excellence
        </h2>
        <p className="text-white/60 font-body mt-4 max-w-2xl mx-auto">
          Experience world-class golf competition combined with Egyptian hospitality
        </p>
      </div>
      
      {/* Timeline */}
      <div className="relative">
        {/* Line */}
        <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-teal-500/50 to-transparent" />
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {EVENT_PROGRAM.map((day, i) => (
            <motion.div
              key={day.day}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className="relative"
              data-testid={`event-day-${day.day}`}
            >
              {/* Connector dot */}
              <div className="hidden lg:block absolute top-0 left-1/2 -translate-x-1/2 -translate-y-8 w-4 h-4 bg-teal-500 rounded-full border-4 border-gray-900" />
              
              <div className="bg-gray-800/50 backdrop-blur border border-gray-700/50 rounded-xl p-5 hover:border-teal-500/50 transition-all h-full">
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-teal-500/20 text-teal-400 text-xs font-bold px-2 py-1 rounded">
                    Day {day.day}
                  </span>
                  <span className="text-white/60 text-xs">{day.date}</span>
                </div>
                <h3 className="font-heading text-lg font-bold text-white mb-3">{day.title}</h3>
                <ul className="space-y-2">
                  {day.activities.map((activity, j) => (
                    <li key={`${day.day}-activity-${j}-${activity.slice(0, 20)}`} className="flex items-start gap-2 text-white/70 text-sm font-body">
                      <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                      {activity}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      
      {/* Tournament Info */}
      <div className="mt-16 grid md:grid-cols-2 gap-8">
        <div className="bg-gradient-to-br from-teal-600/20 to-teal-900/20 border border-teal-500/30 rounded-2xl p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-teal-500/20 rounded-xl flex items-center justify-center">
              <Trophy className="w-7 h-7 text-teal-400" />
            </div>
            <div>
              <h3 className="font-heading text-xl font-bold text-white">Official Tournament</h3>
              <p className="text-white/60 font-body text-sm">For National Teams</p>
            </div>
          </div>
          <ul className="space-y-3">
            <li className="flex items-center gap-3 text-white/80 font-body">
              <CheckCircle className="w-5 h-5 text-teal-500" />
              20 Qualifying Countries
            </li>
            <li className="flex items-center gap-3 text-white/80 font-body">
              <CheckCircle className="w-5 h-5 text-teal-500" />
              Teams of 2 Players
            </li>
            <li className="flex items-center gap-3 text-white/80 font-body">
              <CheckCircle className="w-5 h-5 text-teal-500" />
              Handicap: 1 player &lt;15, 1 player ≥15
            </li>
            <li className="flex items-center gap-3 text-white/80 font-body">
              <CheckCircle className="w-5 h-5 text-teal-500" />
              Individual & Team Rankings
            </li>
          </ul>
        </div>
        
        <div className="bg-gradient-to-br from-orange-600/20 to-orange-900/20 border border-orange-500/30 rounded-2xl p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-orange-500/20 rounded-xl flex items-center justify-center">
              <Users className="w-7 h-7 text-orange-400" />
            </div>
            <div>
              <h3 className="font-heading text-xl font-bold text-white">Guest Tournament</h3>
              <p className="text-white/60 font-body text-sm">Open to All Amateurs</p>
            </div>
          </div>
          <ul className="space-y-3">
            <li className="flex items-center gap-3 text-white/80 font-body">
              <CheckCircle className="w-5 h-5 text-orange-500" />
              Open to Any Amateur Golfer
            </li>
            <li className="flex items-center gap-3 text-white/80 font-body">
              <CheckCircle className="w-5 h-5 text-orange-500" />
              Same Terms & Timing
            </li>
            <li className="flex items-center gap-3 text-white/80 font-body">
              <CheckCircle className="w-5 h-5 text-orange-500" />
              Prizes for Top 3 Per Category
            </li>
            <li className="flex items-center gap-3 text-white/80 font-body">
              <CheckCircle className="w-5 h-5 text-orange-500" />
              Perfect for Companions & Guests
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
);

// Packages Section
const PackagesSection = ({ onBookClick }) => (
  <section id="packages" className="py-20 bg-gray-900">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="text-teal-400 font-body text-sm uppercase tracking-wider">Booking Packages</span>
        <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mt-2">
          All-Inclusive Golf Experience
        </h2>
        <p className="text-white/60 font-body mt-4 max-w-2xl mx-auto">
          5 nights accommodation, 4 rounds of golf, and an unforgettable experience
        </p>
      </div>
      
      <div className="grid md:grid-cols-3 gap-8">
        {PACKAGES.map((pkg, i) => (
          <motion.div
            key={pkg.name}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            viewport={{ once: true }}
            className={`relative rounded-2xl overflow-hidden ${
              pkg.highlight 
                ? 'bg-gradient-to-b from-teal-600/30 to-gray-900 border-2 border-teal-500' 
                : 'bg-gray-800/50 border border-gray-700/50'
            }`}
            data-testid={`package-${pkg.name.toLowerCase().replace(' ', '-')}`}
          >
            {pkg.highlight && (
              <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-teal-500 to-teal-600 text-white text-center py-2 text-sm font-bold">
                MOST POPULAR
              </div>
            )}
            
            <div className={`p-8 ${pkg.highlight ? 'pt-14' : ''}`}>
              <h3 className="font-heading text-2xl font-bold text-white mb-2">{pkg.name}</h3>
              <p className="text-white/60 font-body text-sm mb-6">{pkg.description}</p>
              
              {pkg.isAddon ? (
                <div className="mb-6">
                  <span className="font-heading text-4xl font-bold text-white">${pkg.price}</span>
                  <span className="text-white/60 font-body ml-2">/ person</span>
                </div>
              ) : (
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 font-body text-sm">Golfer (Single)</span>
                    <span className="font-heading text-xl font-bold text-white">${pkg.golferSingle}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 font-body text-sm">Golfer (Double)</span>
                    <span className="font-heading text-xl font-bold text-white">${pkg.golferDouble}/pax</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 font-body text-sm">Non-Golfer (Single)</span>
                    <span className="font-heading text-lg text-white/80">${pkg.nonGolferSingle}</span>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b border-gray-700">
                    <span className="text-white/60 font-body text-sm">Non-Golfer (Double)</span>
                    <span className="font-heading text-lg text-white/80">${pkg.nonGolferDouble}/pax</span>
                  </div>
                  <div className="flex justify-between items-center text-teal-400">
                    <span className="font-body text-sm">Children ≤13 (w/ 2 adults)</span>
                    <span className="font-bold">FREE</span>
                  </div>
                </div>
              )}
              
              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature, j) => (
                  <li key={`${pkg.name}-feature-${j}-${feature.slice(0, 15)}`} className="flex items-center gap-3 text-white/70 font-body text-sm">
                    <CheckCircle className={`w-4 h-4 ${pkg.color === 'secondary' ? 'text-orange-500' : 'text-teal-500'}`} />
                    {feature}
                  </li>
                ))}
              </ul>
              
              <button onClick={onBookClick} className={`w-full py-3 rounded-full font-bold transition-all ${
                pkg.highlight 
                  ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:shadow-lg hover:shadow-teal-500/30' 
                  : 'border border-teal-500 text-teal-400 hover:bg-teal-500/10'
              }`}>
                {pkg.isAddon ? 'Add to Package' : 'Select Package'}
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// Excursions Section
const ExcursionsSection = () => (
  <section id="excursions" className="py-20 bg-black">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="text-teal-400 font-body text-sm uppercase tracking-wider">Optional Excursions</span>
        <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mt-2">
          Explore Ancient Egypt
        </h2>
        <p className="text-white/60 font-body mt-4 max-w-2xl mx-auto">
          Extend your experience with unforgettable cultural and adventure trips
        </p>
      </div>
      
      <div className="grid md:grid-cols-3 gap-8">
        {EXCURSIONS.map((excursion, i) => (
          <motion.div
            key={excursion.name}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            viewport={{ once: true }}
            className="group rounded-2xl overflow-hidden bg-gray-900/50 border border-gray-800 hover:border-teal-500/50 transition-all"
            data-testid={`excursion-${i}`}
          >
            <div className="relative h-48 overflow-hidden">
              <img 
                src={excursion.image} 
                alt={excursion.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="bg-teal-500/90 text-white text-xs font-bold px-3 py-1 rounded-full">
                  {excursion.date}
                </span>
              </div>
            </div>
            
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-xl font-bold text-white">{excursion.name}</h3>
                <div className="text-right">
                  <span className="text-teal-400 font-bold">From ${excursion.price}</span>
                  <span className="text-white/50 text-xs block">per person</span>
                </div>
              </div>
              
              <p className="text-white/60 font-body text-sm mb-4">{excursion.description}</p>
              
              <div className="flex items-center gap-4 mb-4">
                <span className="flex items-center gap-1 text-white/50 text-xs">
                  <Clock className="w-4 h-4" />
                  {excursion.duration}
                </span>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {excursion.includes.map((item, j) => (
                  <span key={j} className="bg-gray-800 text-white/70 text-xs px-3 py-1 rounded-full">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// Winners Gallery Section
const WinnersSection = () => {
  const [activeWinner, setActiveWinner] = useState(0);
  
  return (
    <section id="winners" className="py-20 bg-gradient-to-b from-gray-900 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-teal-400 font-body text-sm uppercase tracking-wider">Hall of Fame</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mt-2">
            Previous Champions
          </h2>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Featured Winner */}
          <motion.div
            key={activeWinner}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="relative rounded-2xl overflow-hidden aspect-[4/3]"
          >
            <img 
              src={PREVIOUS_WINNERS[activeWinner].image} 
              alt={`${PREVIOUS_WINNERS[activeWinner].year} Champions`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <span className="bg-teal-500 text-white font-bold px-4 py-2 rounded-lg text-sm">
                {PREVIOUS_WINNERS[activeWinner].year} Champions
              </span>
              <h3 className="font-heading text-3xl font-bold text-white mt-4">
                Team {PREVIOUS_WINNERS[activeWinner].country}
              </h3>
              <p className="text-white/70 font-body mt-2">
                {PREVIOUS_WINNERS[activeWinner].players.join(' & ')}
              </p>
            </div>
          </motion.div>
          
          {/* Winner List */}
          <div className="space-y-4">
            {PREVIOUS_WINNERS.map((winner, i) => (
              <button
                key={winner.year}
                onClick={() => setActiveWinner(i)}
                className={`w-full p-6 rounded-xl text-left transition-all ${
                  activeWinner === i 
                    ? 'bg-teal-500/20 border-2 border-teal-500' 
                    : 'bg-gray-800/50 border border-gray-700/50 hover:border-gray-600'
                }`}
                data-testid={`winner-${winner.year}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-teal-400 font-bold text-sm">{winner.year}</span>
                    <h4 className="font-heading text-xl font-bold text-white mt-1">Team {winner.country}</h4>
                    <p className="text-white/60 font-body text-sm">{winner.players.join(' & ')}</p>
                  </div>
                  <Trophy className={`w-8 h-8 ${activeWinner === i ? 'text-teal-400' : 'text-gray-600'}`} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Video Section
const VideoSection = () => (
  <section id="video" className="py-20 bg-gradient-to-b from-gray-900 to-black">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <span className="text-teal-400 font-body text-sm uppercase tracking-wider">Featured Video</span>
        <h2 className="font-heading text-4xl font-bold text-white mt-2">
          Road to SomaBay 2026
        </h2>
        <p className="text-white/60 font-body mt-4 max-w-2xl mx-auto">
          Every Swing Counts, Every Shot Matters. Watch the launch of Kenya&apos;s journey to the SomaBay World Cup.
        </p>
      </div>
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        className="relative max-w-4xl mx-auto"
      >
        <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-teal-500/20 border border-white/10">
          <video
            controls
            className="w-full aspect-video bg-black"
            poster="https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
            data-testid="somabay-launch-video"
          >
            <source src="https://customer-assets.emergentagent.com/job_6bdd03ec-2e3d-4c40-849f-33d024d87b20/artifacts/0luskey0_VID-20260330-WA0047.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        
        {/* Video info */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-white/60 font-body text-sm">
          <span className="flex items-center gap-2">
            <Calendar size={16} className="text-teal-400" />
            Limuru Country Club Launch
          </span>
          <span className="flex items-center gap-2">
            <MapPin size={16} className="text-teal-400" />
            12 Qualifiers Nationwide
          </span>
          <span className="flex items-center gap-2">
            <Trophy size={16} className="text-teal-400" />
            2 Players to Egypt
          </span>
        </div>
      </motion.div>
    </div>
  </section>
);

// Articles Section
const ArticlesSection = () => {
  const [selectedArticle, setSelectedArticle] = useState(null);

  return (
    <>
      <section id="articles" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-teal-400 font-body text-sm uppercase tracking-wider">Latest News</span>
              <h2 className="font-heading text-4xl font-bold text-white mt-2">
                Articles & Updates
              </h2>
            </div>
            <Link to="#" className="hidden sm:flex items-center gap-2 text-teal-400 font-body hover:underline">
              View All <ChevronRight size={18} />
            </Link>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ARTICLES.map((article, i) => (
              <motion.article
                key={article.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
                onClick={() => setSelectedArticle(article)}
                data-testid={`article-${i}`}
              >
                <div className="relative h-48 rounded-xl overflow-hidden mb-4">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute top-4 left-4">
                    <span className="bg-black/70 text-teal-400 text-xs font-bold px-3 py-1 rounded-full">
                      {article.category}
                    </span>
                  </div>
                  {article.featured && (
                    <div className="absolute top-4 right-4">
                      <span className="bg-teal-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                        FEATURED
                      </span>
                    </div>
                  )}
                  {/* Read More overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="bg-teal-500 text-white text-sm font-bold px-4 py-2 rounded-full flex items-center gap-2">
                      Read More <ArrowRight size={16} />
                    </span>
                  </div>
                </div>
                <span className="text-white/50 text-xs font-body">{article.date}</span>
                <h3 className="font-heading text-lg font-bold text-white mt-2 group-hover:text-teal-400 transition-colors line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-white/60 font-body text-sm mt-2 line-clamp-2">{article.excerpt}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
      
      {/* Article Detail Modal */}
      <ArticleDetailModal 
        article={selectedArticle} 
        isOpen={!!selectedArticle} 
        onClose={() => setSelectedArticle(null)} 
      />
    </>
  );
};

// CTA Section
const CTASection = ({ onBookClick }) => (
  <section className="py-20 bg-gradient-to-r from-teal-600 to-teal-800 relative overflow-hidden">
    <div className="absolute inset-0 opacity-10">
      <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
    </div>
    
    <div className="relative max-w-4xl mx-auto px-4 text-center">
      <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
        Ready for the Experience of a Lifetime?
      </h2>
      <p className="text-white/80 font-body text-lg mb-8 max-w-2xl mx-auto">
        Secure your spot at the SomaBay World Cup 2026. Limited places available.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button onClick={onBookClick} className="bg-white text-teal-600 font-bold px-8 py-4 rounded-full hover:shadow-xl transition-all" data-testid="cta-register-btn">
          Register Your Team
        </button>
        <button className="border-2 border-white text-white font-bold px-8 py-4 rounded-full hover:bg-white/10 transition-all">
          Contact Us
        </button>
      </div>
    </div>
  </section>
);

// Footer
const SomaBayFooter = () => (
  <footer className="bg-gray-900 border-t border-gray-800 py-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-heading text-lg font-bold text-white block leading-tight">SOMABAY</span>
              <span className="font-heading text-xs text-teal-400">WORLD CUP</span>
            </div>
          </div>
          <p className="text-white/50 font-body text-sm">
            The premier international golf tournament in Egypt&apos;s Red Sea paradise.
          </p>
        </div>
        
        <div>
          <h4 className="font-heading font-bold text-white mb-4">Quick Links</h4>
          <ul className="space-y-2">
            <li><a href="#event" className="text-white/60 hover:text-teal-400 font-body text-sm">Event Program</a></li>
            <li><a href="#packages" className="text-white/60 hover:text-teal-400 font-body text-sm">Packages</a></li>
            <li><a href="#excursions" className="text-white/60 hover:text-teal-400 font-body text-sm">Excursions</a></li>
            <li><a href="#winners" className="text-white/60 hover:text-teal-400 font-body text-sm">Hall of Fame</a></li>
          </ul>
        </div>
        
        <div>
          <h4 className="font-heading font-bold text-white mb-4">Contact</h4>
          <ul className="space-y-2 text-white/60 font-body text-sm">
            <li>
              <a href="mailto:golf@michezonet.co.ke" className="hover:text-teal-400 transition-colors">
                golf@michezonet.co.ke
              </a>
            </li>
          </ul>
        </div>
        
        <div>
          <h4 className="font-heading font-bold text-white mb-4">Organized By</h4>
          <Link to="/" className="flex items-center gap-2">
            <span className="font-heading text-xl font-bold">
              <span className="text-primary">MICHEZO</span>
              <span className="text-secondary">NET</span>
            </span>
          </Link>
          <p className="text-white/50 font-body text-sm mt-2">Your Passion is Our Business</p>
        </div>
      </div>
      
      <div className="border-t border-gray-800 mt-8 pt-8 text-center">
        <p className="text-white/40 font-body text-sm">
          © 2026 SomaBay World Cup. Powered by Michezonet.
        </p>
      </div>
    </div>
  </footer>
);

// Main Page Component
const RoadToSomaBay = () => {
  const [showBookingModal, setShowBookingModal] = useState(false);
  
  return (
    <div className="min-h-screen bg-black" data-testid="road-to-somabay">
      <SomaBayHeader onBookClick={() => setShowBookingModal(true)} />
      <HeroSection onBookClick={() => setShowBookingModal(true)} />
      <CircuitScheduleSection />
      <EventCalendarSection />
      <PackagesSection onBookClick={() => setShowBookingModal(true)} />
      <ExcursionsSection />
      <WinnersSection />
      <VideoSection />
      <ArticlesSection />
      <CTASection onBookClick={() => setShowBookingModal(true)} />
      <SomaBayFooter />
      <BookingFormModal isOpen={showBookingModal} onClose={() => setShowBookingModal(false)} />
    </div>
  );
};

export default RoadToSomaBay;
;
